import React, { useState } from 'react';
import { Clock, FileText, LogOut, MessageCircle, ShieldAlert } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { UserSuspension } from '../../../types';
import { functionsClient } from '../../../backend/firebase';
import { SUSPENSION_NOTICE_TEXT } from '../../../../functions/src/moderationConfig';

interface SuspensionScreenProps {
  suspension: UserSuspension;
  onLogout: () => void;
  onReadPolicies: () => void;
}

const formatDate = (value: unknown): string => {
  if (value && typeof value === 'object' && 'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toLocaleString();
  }
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }
  return '—';
};

export const SuspensionScreen: React.FC<SuspensionScreenProps> = ({
  suspension,
  onLogout,
  onReadPolicies
}) => {
  const [language, setLanguage] = useState<'en' | 'hi'>('en');
  const [appealOpen, setAppealOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const text = SUSPENSION_NOTICE_TEXT[language];
  const isPermanent = suspension.type === 'permanent' || suspension.endsAt === null;
  const typeLabel = isPermanent ? text.permanent
    : suspension.type === 'long_term' ? text.longTerm : text.temporary;
  const reasonText = suspension.reasonCategory
    ? text.reasons[suspension.reasonCategory]
    : (language === 'en' ? 'Your account has been paused.' : 'Aapka account rok diya gaya hai.');

  const submitAppeal = async (event: React.FormEvent) => {
    event.preventDefault();
    const cleanMessage = message.trim();
    if (!cleanMessage || cleanMessage.length > 1000) {
      setNotice(language === 'en' ? 'Enter a message of 1 to 1000 characters.' : '1 se 1000 characters ka message likhein.');
      return;
    }
    setSubmitting(true);
    setNotice('');
    try {
      await httpsCallable(functionsClient, 'createSuspensionAppeal')({ message: cleanMessage });
      setNotice(text.appealSubmitted);
      setMessage('');
      setAppealOpen(false);
    } catch (error) {
      console.error('Could not submit suspension appeal:', error);
      setNotice(language === 'en' ? 'We could not submit your appeal. Please try again.' : 'Appeal bheji nahi ja saki. Dobara koshish karein.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto bg-slate-950 p-5 text-white">
      <div className="mx-auto flex min-h-full max-w-lg items-center justify-center py-6">
        <main className="w-full space-y-6 text-center">
          <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-rose-500 bg-rose-500/20">
            <ShieldAlert className="h-10 w-10 text-rose-400" />
          </div>

          <div className="space-y-3">
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setLanguage('en')}
                aria-pressed={language === 'en'}
                className={`rounded-lg px-3 py-1 text-xs font-bold ${language === 'en' ? 'bg-white text-slate-900' : 'bg-white/10 text-slate-300'}`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('hi')}
                aria-pressed={language === 'hi'}
                className={`rounded-lg px-3 py-1 text-xs font-bold ${language === 'hi' ? 'bg-white text-slate-900' : 'bg-white/10 text-slate-300'}`}
              >
                Hinglish
              </button>
            </div>
            <h1 className="text-2xl font-black tracking-tight">{text.title}</h1>
            <p className="text-sm font-semibold text-rose-300">{typeLabel}</p>
            <p className="text-sm leading-relaxed text-slate-300">{reasonText}</p>
          </div>

          <section className="space-y-4 rounded-3xl border border-white/10 bg-white/5 p-5 text-left">
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-400">{text.startsAt}: </span>{formatDate(suspension.startsAt || suspension.suspendedAt)}</p>
                <p>
                  <span className="text-slate-400">{text.endsAt}: </span>
                  {isPermanent ? text.permanentUntil : formatDate(suspension.endsAt || suspension.suspendedUntil)}
                </p>
                <p className="break-all"><span className="text-slate-400">{text.reference}: </span>{suspension.referenceId || '—'}</p>
              </div>
            </div>
            <p className="border-t border-white/10 pt-4 text-xs leading-relaxed text-slate-300">{text.canStill}</p>
          </section>

          {notice && <p role="status" className="rounded-xl bg-white/10 p-3 text-sm text-slate-200">{notice}</p>}

          {appealOpen && (
            <form onSubmit={submitAppeal} className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-5 text-left">
              <label htmlFor="suspension-appeal" className="block text-sm font-bold">{text.appealMessage}</label>
              <textarea
                id="suspension-appeal"
                maxLength={1000}
                rows={5}
                value={message}
                onChange={event => setMessage(event.target.value)}
                className="w-full resize-y rounded-xl border border-white/15 bg-slate-900 p-3 text-sm outline-none focus:border-amber-300"
                required
              />
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{message.length}/1000</span>
                <button
                  disabled={submitting}
                  className="rounded-xl bg-amber-400 px-4 py-2 font-black text-slate-950 disabled:opacity-50"
                >
                  {submitting ? '…' : text.submitAppeal}
                </button>
              </div>
            </form>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <button onClick={onReadPolicies} className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-xs font-bold hover:bg-white/10">
              <FileText className="h-4 w-4" /> {text.policies}
            </button>
            <button onClick={() => setAppealOpen(open => !open)} className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-xs font-bold hover:bg-white/10">
              <MessageCircle className="h-4 w-4" /> {text.appeal}
            </button>
            <button onClick={onLogout} className="flex items-center justify-center gap-2 rounded-2xl bg-white px-3 py-3 text-xs font-black text-slate-950 hover:bg-slate-200">
              <LogOut className="h-4 w-4" /> {text.logout}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};
